import React, { useCallback, useMemo, useState } from "react";
import { PageSection } from "@/components/AppLayout.jsx";
import { AdminGuard } from "@/components/admin/AdminGuard.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { useHybridDataStore } from "@/hooks/useHybridDataStore.js";
import { useAuth } from "@/context/AuthContext.jsx";
import {
  apiFetchAvatarDiagnostics,
  apiFetchMe,
  apiRepairAvatarFromDisk,
  getStoredBackendUser,
} from "@/api/karateBackendApi.js";
import { resolveBackendAvatarUrl } from "@/services/karateAvatarStorage.js";
import { isHybridDataMode } from "@/config/backendStaging049.js";

export function AdminAvatarDiagnosticPage() {
  const { ui, locale } = useKarateUi();
  const d = ui.avatarDiagnostic;
  const { store, backendStatus, refreshBackend } = useHybridDataStore();
  const { username } = useAuth();
  const [diag, setDiag] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [imageStatus, setImageStatus] = useState(null);

  const personId = getStoredBackendUser()?.personId || getStoredBackendUser()?.person?.id || null;

  const headerSource = useMemo(() => {
    const backendUser = getStoredBackendUser();
    const url = backendUser?.person?.avatarUrl || backendUser?.person?.avatar_url || null;
    return url ? d.sourceBackend : d.sourceNone;
  }, [diag, d.sourceBackend, d.sourceNone]);

  const instructorSource = useMemo(() => {
    if (backendStatus !== "backend") return d.sourceInstructorNone;
    const gilles = store.instructeurs?.find((i) => i.id === "inst-gilles-lavigne" || i.id === personId);
    return gilles?.avatar_url ? d.sourceInstructorBackend : d.sourceInstructorNone;
  }, [backendStatus, store.instructeurs, personId, d.sourceInstructorBackend, d.sourceInstructorNone]);

  const previewUrl = diag?.urls?.authMeAvatarUrl ? resolveBackendAvatarUrl(diag.urls.authMeAvatarUrl) : null;

  const runVerify = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      if (!isHybridDataMode()) throw new Error("Hybrid mode required");
      const [result] = await Promise.all([apiFetchAvatarDiagnostics(), apiFetchMe(), refreshBackend()]);
      setDiag(result);
    } catch (e) {
      setMessage(e?.message || "Diagnostic failed");
      setDiag({ ok: false, blockingReasons: ["diagnostic_request_failed"] });
    } finally {
      setBusy(false);
    }
  }, [refreshBackend]);

  const runRepair = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await apiRepairAvatarFromDisk();
      setMessage(result.repaired ? d.repairSuccess : d.repairFailed);
      await runVerify();
    } catch {
      setMessage(d.repairFailed);
    } finally {
      setBusy(false);
    }
  }, [d.repairFailed, d.repairSuccess, runVerify]);

  const testImageGet = useCallback(async () => {
    if (!previewUrl) {
      setImageStatus(404);
      return;
    }
    try {
      const res = await fetch(previewUrl);
      setImageStatus(res.status);
    } catch {
      setImageStatus(0);
    }
  }, [previewUrl]);

  return (
    <AdminGuard>
      <PageSection title={d.title} subtitle={d.subtitle} testId="page-admin-diagnostic-avatar">
        <div className="karate-avatar-diagnostic" data-testid="karate-avatar-diagnostic-panel">
          <div className="karate-avatar-diagnostic__actions">
            <button type="button" className="btn btn--primary" onClick={runVerify} disabled={busy} data-testid="karate-avatar-diagnostic-verify">
              {busy ? d.loading : d.verify}
            </button>
            <button type="button" className="btn" onClick={runRepair} disabled={busy} data-testid="karate-avatar-diagnostic-repair">
              {d.repair}
            </button>
            <button type="button" className="btn" onClick={testImageGet} disabled={busy || !previewUrl} data-testid="karate-avatar-diagnostic-test-image">
              {d.testImage}
            </button>
          </div>

          {diag ? (
            <p
              className={diag.ok ? "karate-avatar-diagnostic__verdict karate-avatar-diagnostic__verdict--pass" : "karate-avatar-diagnostic__verdict karate-avatar-diagnostic__verdict--fail"}
              data-testid="karate-avatar-diagnostic-verdict"
              data-ok={diag.ok ? "true" : "false"}
            >
              {diag.ok ? d.pass : d.fail}
            </p>
          ) : null}

          {message ? <p className="karate-avatar-diagnostic__msg">{message}</p> : null}

          <dl className="karate-avatar-diagnostic__sources">
            <div>
              <dt>{d.headerSource}</dt>
              <dd data-testid="karate-avatar-diagnostic-header-source">{headerSource}</dd>
            </div>
            <div>
              <dt>{d.instructorCardSource}</dt>
              <dd data-testid="karate-avatar-diagnostic-instructor-source">{instructorSource}</dd>
            </div>
            {username ? (
              <div>
                <dt>User</dt>
                <dd>{username}</dd>
              </div>
            ) : null}
          </dl>

          {previewUrl ? (
            <div className="karate-avatar-diagnostic__preview" data-testid="karate-avatar-diagnostic-preview">
              <p>{d.backendPreview}</p>
              <img src={previewUrl} alt="" className="karate-avatar-diagnostic__img" />
              {imageStatus != null ? <p data-testid="karate-avatar-diagnostic-image-status">GET {imageStatus}</p> : null}
            </div>
          ) : null}

          {diag?.blockingReasons?.length ? (
            <div data-testid="karate-avatar-diagnostic-blocking">
              <h3>{d.blockingReasons}</h3>
              <ul>
                {diag.blockingReasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {diag ? (
            <details>
              <summary>{d.rawJson}</summary>
              <pre data-testid="karate-avatar-diagnostic-json">{JSON.stringify(diag, null, 2)}</pre>
            </details>
          ) : null}
        </div>
      </PageSection>
    </AdminGuard>
  );
}
